import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImage, faUpload, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import api from '../../../../../services/apiService';
import { fixImageUrl } from '../../../../../services/imageService';

/**
 * Modal for managing event header image
 * 
 * @param {boolean} show - Whether to show the modal
 * @param {function} onHide - Function to call when hiding the modal
 * @param {object} event - Event data object
 * @param {function} onImageUpdate - Callback when image is updated or deleted
 * @returns {JSX.Element} Modal component
 */
const HeaderImageModal = ({ show, onHide, event, onImageUpdate }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Initialize preview URL when modal is shown
  useEffect(() => {
    if (show && event?.has_header_image) {
      // Construct the header image URL
      const headerImageUrl = `/events/${event.id}/header-image`;
      setPreviewUrl(fixImageUrl(headerImageUrl));
    } else {
      setPreviewUrl(null);
    }
    setSelectedFile(null);
    setError(null);
  }, [show, event]);

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setError('Please select a valid image file (JPG, PNG, or GIF)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image file size must be less than 5MB');
      return;
    }

    // Clear any previous error
    setError(null);
    setSelectedFile(file);

    // Create a preview URL
    const fileReader = new FileReader();
    fileReader.onload = () => {
      setPreviewUrl(fileReader.result);
    };
    fileReader.readAsDataURL(file);
  };

  // Handle image upload
  const handleUpload = async () => {
    if (!selectedFile) {
      setError('Please select a file to upload');
      return;
    }

    setLoading(true);
    setError(null);

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await api.post(`/events/${event.id}/header-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      
      // Call the callback with the updated event data
      if (onImageUpdate) {
        onImageUpdate(response.data);
      }
      
      setLoading(false);
      onHide();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to upload image. Please try again.');
      setLoading(false);
    }
  };

  // Handle image deletion
  const handleDeleteImage = async () => {
    if (!event?.has_header_image) return;
    
    // Confirm deletion
    if (!window.confirm('Are you sure you want to remove this header image?')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.delete(`/events/${event.id}/header-image`);

      
      // Clear preview
      setPreviewUrl(null);
      setSelectedFile(null);
      
      // Call the callback with the updated event data (has_header_image set to false)
      if (onImageUpdate) {
        onImageUpdate({
          ...event,
          has_header_image: false,
          header_image_name: null,
          header_image_type: null,
          header_image_uploaded_at: null
        });
      }
      
      setLoading(false);
      onHide();
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to delete image. Please try again.');
      setLoading(false);
    }
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      centered
      contentClassName="bg-dark text-white"
    >
      <Modal.Header closeButton className="bg-dark text-white">
        <Modal.Title style={{ fontSize: '1.2rem' }}>
          {event?.has_header_image ? "Update Header Image" : "Add Header Image"}
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body className="bg-dark text-white">
        {/* Image Preview */}
        {previewUrl && (
          <div className="text-center mb-3">
            <img 
              src={previewUrl} 
              alt="Header preview" 
              className="img-fluid header-image-preview" 
              style={{ 
                maxHeight: '200px', 
                width: '100%', 
                objectFit: 'cover',
                borderRadius: '8px'
              }}
            />
          </div>
        )}

        {/* File Upload Form */}
        <Form>
          <Form.Group controlId="headerImage">
            <Form.Label>Select Image</Form.Label>
            <Form.Control 
              type="file" 
              onChange={handleFileChange}
              accept="image/jpeg,image/png,image/gif"
              className="bg-secondary text-white"
            />
            <Form.Text className="text-muted">
              Supported formats: JPG, PNG, GIF. Max size: 5MB.
            </Form.Text>
          </Form.Group>

          {error && (
            <div className="alert alert-danger mt-3">{error}</div>
          )}
        </Form>
      </Modal.Body>
      
      <Modal.Footer className="bg-dark text-white justify-content-center">
        {event?.has_header_image && (
          <Button 
            variant="outline-danger" 
            onClick={handleDeleteImage}
            disabled={loading}
            size="sm"
            style={{ width: '100px' }}
          >
            <FontAwesomeIcon icon={faTrashAlt} /> Remove
          </Button>
        )}
        <Button 
          variant="secondary" 
          onClick={onHide} 
          disabled={loading}
          size="sm"
          style={{ width: '100px' }}
        >
          Cancel
        </Button>
        <Button 
          variant="primary" 
          onClick={handleUpload}
          disabled={!selectedFile || loading}
          size="sm"
          style={{ width: '100px' }}
        >
          {loading ? (
            <>
              <Spinner animation="border" size="sm" /> Uploading...
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faUpload} /> Upload
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default HeaderImageModal;